import { get, post } from '@/api/client';
import i18n from '@/i18n';
import { useGame } from '@/store/game';
// PWA: регистрация service worker (обновление по запросу), установка на домашний экран, Web Push.
import { nativeApp, nativeBridge } from './native';
import { platform } from './platform';

let deferredPrompt: (Event & { prompt: () => Promise<void> }) | null = null;
let waitingWorker: ServiceWorker | null = null;

export function registerServiceWorker(): void {
  // Внутри площадок (и в Android-приложении: игра и так лежит в APK) офлайн-кэш и установка не нужны
  if (!('serviceWorker' in navigator) || import.meta.env.DEV || platform || nativeApp) return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as Event & { prompt: () => Promise<void> };
  });
  void navigator.serviceWorker.register('/sw.js', { scope: '/' }).then((reg) => {
    // Новая версия игры включается сразу (страница перезагрузится по controllerchange). Раньше она ждала
    // закрытия всех вкладок, и после обновления сайта игрок подолгу видел старую сборку из кэша.
    const onWaiting = (w: ServiceWorker | null) => {
      if (!w) return;
      waitingWorker = w;
      useGame.getState().toast(i18n.t('common:update'), 'info');
      applyUpdate();
    };
    if (reg.waiting) onWaiting(reg.waiting);
    // Проверять обновление и при возврате во вкладку (PWA на телефоне может жить днями)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void reg.update().catch(() => undefined);
    });
    reg.addEventListener('updatefound', () => {
      const nw = reg.installing;
      nw?.addEventListener('statechange', () => {
        if (nw.state === 'installed' && navigator.serviceWorker.controller) onWaiting(nw);
      });
    });
  });
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    location.reload();
  });
}

export function applyUpdate(): void {
  waitingWorker?.postMessage({ type: 'SKIP_WAITING' });
}

export function canInstall(): boolean {
  return !!deferredPrompt;
}

export async function installApp(): Promise<void> {
  await deferredPrompt?.prompt();
  deferredPrompt = null;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Состояние уведомлений на этом устройстве. В приложении браузерных пушей нет: уведомления показывает
 * сам Android (мост KosApp), старый APK без этих методов надо обновить.
 */
export type PushState = 'on' | 'off' | 'blocked' | 'update_app' | 'unsupported';

const PUSH_WANTED = 'sk_push_wanted';
const PUSH_REFUSED = 'sk_push_refused';
const PUSH_OFFERED_AT = 'sk_push_offered_at';
const OFFER_EVERY_MS = 3 * 86_400_000;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
    if (key === PUSH_WANTED) localStorage.removeItem(PUSH_REFUSED);
  } catch {
    // приватный режим
  }
}

function forget(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // приватный режим
  }
}

function webPushSupported(): boolean {
  // Внутри площадок service worker не регистрируется (см. registerServiceWorker)
  return (
    !platform &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    !import.meta.env.DEV
  );
}

export async function pushState(): Promise<PushState> {
  const bridge = nativeBridge();
  if (bridge) return bridge.notificationState?.() ?? 'update_app';
  if (!webPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'off';
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ? 'on' : 'off';
}

const NATIVE_ANSWER_TIMEOUT = 60_000;

/** Спросить разрешение у Android и дождаться ответа игрока. */
function askNativePermission(ask: () => void): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      window.removeEventListener('kos-notifications', done);
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(done, NATIVE_ANSWER_TIMEOUT);
    window.addEventListener('kos-notifications', done);
    ask();
  });
}

/** Привязать телефон к текущему аккаунту; разрешение Android уже есть. */
async function linkNativeDevice(bridge: NonNullable<ReturnType<typeof nativeBridge>>): Promise<void> {
  const { token } = await post<{ token: string }>('/push/device', {
    token: bridge.pushToken?.() || undefined,
  });
  bridge.setPushToken?.(token);
}

async function enableNativePush(bridge: NonNullable<ReturnType<typeof nativeBridge>>): Promise<PushState> {
  if (!bridge.requestNotifications || !bridge.setPushToken) return 'update_app';
  await askNativePermission(() => bridge.requestNotifications?.());
  const state = await pushState();
  if (state === 'blocked') return state;
  await linkNativeDevice(bridge);
  // Сразу видно, что телефон показывает уведомления игры
  bridge.showNotification?.('Kings of Seas', i18n.t('game:profile.pushStatus.testNotification'));
  return pushState();
}

/** Включить уведомления на этом устройстве; возвращает, что получилось. */
export async function enablePush(): Promise<PushState> {
  const state = await turnPushOn();
  if (state === 'on') remember(PUSH_WANTED, '1');
  return state;
}

async function turnPushOn(): Promise<PushState> {
  const bridge = nativeBridge();
  if (bridge) return enableNativePush(bridge);
  if (!webPushSupported()) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'blocked';
  if (permission !== 'granted') return 'off';
  const { publicKey } = await get<{ publicKey: string | null }>('/push/vapid');
  if (!publicKey) return 'unsupported';
  const reg = await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await post('/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
  return 'on';
}

/** Игрок сам выключил уведомления: больше не включаем и не предлагаем. */
export async function disablePush(): Promise<void> {
  forget(PUSH_WANTED);
  remember(PUSH_REFUSED, '1');
  await unlinkPush();
}

/**
 * Отвязать устройство от аккаунта при выходе, пока сессия жива: иначе уведомления этого игрока
 * придут следующему. Желание получать их остаётся: при следующем входе syncPush подключит снова.
 */
export async function unlinkPush(): Promise<void> {
  const bridge = nativeBridge();
  if (bridge) {
    const token = bridge.pushToken?.();
    if (!token) return;
    bridge.setPushToken?.('');
    await post('/push/device/remove', { token });
    return;
  }
  if (!webPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await post('/push/unsubscribe', { endpoint: sub.endpoint });
  await sub.unsubscribe();
}

/**
 * При каждом входе в игру: устройство, где уведомления включены, привязывается к текущему аккаунту.
 * Без этого смена аккаунта молча выключала бы уведомления навсегда.
 */
export async function syncPush(): Promise<void> {
  const bridge = nativeBridge();
  if (bridge) {
    const state = bridge.notificationState?.();
    if (bridge.pushToken?.()) remember(PUSH_WANTED, '1');
    if (state === 'blocked' || !bridge.setPushToken) return;
    if (read(PUSH_WANTED)) await linkNativeDevice(bridge);
    return;
  }
  if (!webPushSupported() || Notification.permission !== 'granted') return;
  if ((await pushState()) === 'on') remember(PUSH_WANTED, '1');
  if (read(PUSH_WANTED)) await enablePush();
}

/** Показать предложение включить уведомления: первый раз, потом раз в 3 дня, пока не решит. */
export async function shouldOfferPush(): Promise<boolean> {
  if (read(PUSH_REFUSED) || Date.now() - Number(read(PUSH_OFFERED_AT) ?? 0) < OFFER_EVERY_MS) return false;
  return (await pushState()) === 'off';
}

export function pushOffered(): void {
  remember(PUSH_OFFERED_AT, String(Date.now()));
}
